import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trophy, Globe, AlertTriangle, RotateCw, ChevronDown, Plug } from "lucide-react";
import { api } from "../lib/api";
import { RankChange, RatingValue } from "../components/Rating";
import { SimBadge } from "../components/Badges";
import { CinematicPortrait } from "../components/CinematicPortrait";
import { Flag } from "../components/Flag";

const CONTAINER = "mx-auto max-w-[1680px] px-4 sm:px-8 lg:px-12";
const PAGE = 25;
const POS_ART = ["/assets/home/players/player-01.webp", "/assets/home/players/player-02.webp", "/assets/home/players/player-03.webp"];

const PARTNER_SYSTEMS = [
  { key: "overall", label: "Overall", prod: "OVERALL" },
  { key: "arenabook", label: "ArenaBook", prod: "ARENABOOK" },
  { key: "challenge", label: "Challenge", prod: "CHALLENGE" },
  { key: "strike5", label: "Strike 5", prod: "STRIKE5" },
  { key: "sportsbook", label: "Sportsbook", prod: "SPORTSBOOK" },
];
const ENTITIES = [{ key: "players", label: "Players" }, { key: "clubs", label: "Clubs" }, { key: "nations", label: "Nations" }];
// Public (non-partner) ranking system options per entity. Players gets SPORTSBOOK
// as a real discipline (Phase 4, SPORTSBOOK_V1).
const RANKING_SYSTEMS = {
  players: [
    { key: "overall", label: "Overall" },
    { key: "arenabook", label: "ArenaBook" },
    { key: "strike5", label: "Strike 5" },
    { key: "challenge", label: "Challenge" },
    { key: "sportsbook", label: "Sportsbook" },
  ],
  clubs: [{ key: "overall", label: "Overall" }],
  nations: [{ key: "overall", label: "Overall" }],
};
const SPORTSBOOK_SUBS = [
  { key: "ALL", label: "All" },
  { key: "PREGAME", label: "Pregame" },
  { key: "LIVE", label: "Live" },
];
const SCOPES = { players: ["world", "country", "partner"], clubs: ["world", "country"], nations: ["world"] };
const BASE = { arenabook: "players", overall: "overall", strike5: "strike5", challenge: "challenge" };
const METRIC_LABEL = { arenabook: "Duels", strike5: "Rounds", challenge: "Matches", overall: "Results", sportsbook: "Settled" };

function scopeKey(entity, ranking, scope) {
  const suffix = scope === "world" ? "world" : "greece";
  if (entity === "clubs") return `clubs_${suffix}`;
  if (entity === "nations") return "nations_world";
  return `${BASE[ranking]}_${suffix}`;
}

function legacyToModel(sc) {
  const m = {
    players_world: ["players", "arenabook", "world"], players_greece: ["players", "arenabook", "country"],
    strike5_world: ["players", "strike5", "world"], strike5_greece: ["players", "strike5", "country"],
    challenge_world: ["players", "challenge", "world"], challenge_greece: ["players", "challenge", "country"],
    overall_world: ["players", "overall", "world"], overall_greece: ["players", "overall", "country"],
    clubs_world: ["clubs", "overall", "world"], clubs_greece: ["clubs", "overall", "country"],
    nations_world: ["nations", "overall", "world"],
  };
  return m[sc];
}

function Seg({ options, value, onChange, testidPrefix }) {
  return (
    <div className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512]">
      {options.map((o) => (
        <button key={o.key} data-testid={`${testidPrefix}-${o.key}`} onClick={() => onChange(o.key)}
          className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-heading font-bold uppercase tracking-wide whitespace-nowrap ${value === o.key ? "bg-[#E5B842] text-[#000000]" : "text-slate-300 hover:text-[#E5B842]"}`}
          style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">{label}</span>
      <div className="overflow-x-auto no-scrollbar">{children}</div>
    </div>
  );
}

export default function RankingsPage() {
  const [sp, setSp] = useSearchParams();

  const init = (() => {
    const legacy = sp.get("scope");
    if (legacy && legacyToModel(legacy)) {
      const [e, r, s] = legacyToModel(legacy);
      return { entity: e, ranking: r, scope: s, country: sp.get("country") || "" };
    }
    return {
      entity: sp.get("entity") || "players",
      ranking: sp.get("ranking") || "overall",
      scope: sp.get("scope") || "world",
      country: sp.get("country") || "",
      partner: sp.get("partner") || "",
      subtype: (sp.get("subtype") || "ALL").toUpperCase(),
    };
  })();

  const [entity, setEntity] = useState(ENTITIES.some((e) => e.key === init.entity) ? init.entity : "players");
  const [ranking, setRanking] = useState(init.ranking);
  const [scope, setScope] = useState(init.scope);
  const [country, setCountry] = useState(init.country);
  const [countries, setCountries] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partner, setPartner] = useState(init.partner || "");
  const [subtype, setSubtype] = useState(init.subtype || "ALL");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [skip, setSkip] = useState(0);
  const reqId = useRef(0);

  const systems = scope === "partner" ? PARTNER_SYSTEMS : RANKING_SYSTEMS[entity];
  const allowedScopes = SCOPES[entity] || ["world"];

  // Keep selections valid when entity or scope changes
  useEffect(() => {
    if (!systems.some((s) => s.key === ranking)) setRanking(systems[0].key);
    if (!allowedScopes.includes(scope)) setScope("world");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, scope]);

  // Load partners (public) when scope flips to PARTNER
  useEffect(() => {
    if (scope !== "partner") return;
    api.get("/partners/public").then(({ data }) => {
      setPartners(data.partners || []);
      setPartner((prev) => prev && data.partners?.some((p) => p.slug === prev) ? prev : (data.partners?.[0]?.slug || ""));
    }).catch(() => setPartners([]));
  }, [scope]);

  // Load countries when scope flips to COUNTRY
  useEffect(() => {
    if (scope !== "country") return;
    const natKey = scopeKey(entity, ranking, "country");
    api.get(`/rankings/countries?scope=${natKey}`).then(({ data }) => {
      setCountries(data.countries || []);
      setCountry((prev) => (data.countries?.some((c) => c.iso3 === prev) ? prev : (data.countries?.[0]?.iso3 || "")));
    }).catch(() => setCountries([]));
  }, [entity, ranking, scope]);

  // Sync URL
  useEffect(() => {
    const next = { entity, ranking, scope };
    if (scope === "country" && country) next.country = country;
    if (scope === "partner") {
      if (partner) next.partner = partner;
      if (ranking === "sportsbook" && subtype && subtype !== "ALL") next.subtype = subtype;
    }
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, ranking, scope, country, partner, subtype]);

  const load = useCallback(() => {
    if (scope === "country" && !country) return;
    if (scope === "partner" && !partner) return;
    setData(null); setError(false);
    const myId = ++reqId.current;
    let url;
    const isPublicSportsbook = scope !== "partner" && ranking === "sportsbook";
    if (scope === "partner") {
      const prod = (PARTNER_SYSTEMS.find((s) => s.key === ranking)?.prod) || "OVERALL";
      url = `/embed/rankings/${encodeURIComponent(partner)}/data?product=${prod}&page=${Math.floor(skip / PAGE) + 1}&limit=${PAGE}`;
      if (prod === "SPORTSBOOK") url += `&subtype=${subtype}`;
    } else if (isPublicSportsbook) {
      const sc = scope === "country" ? "COUNTRY" : "GLOBAL";
      url = `/rankings/sportsbook?scope=${sc}&subtype=${subtype || "ALL"}&page=${Math.floor(skip / PAGE) + 1}&limit=${PAGE}`;
      if (scope === "country" && country) url += `&country=${country}`;
    } else {
      url = `/rankings?scope=${scopeKey(entity, ranking, scope)}&limit=${PAGE}&skip=${skip}`;
      if (scope === "country" && country) url += `&country=${country}`;
    }
    api.get(url)
      .then(({ data }) => { if (myId === reqId.current) setData(data); })
      .catch(() => { if (myId === reqId.current) setError(true); });
  }, [entity, ranking, scope, country, partner, subtype, skip]);

  useEffect(() => { setSkip(0); }, [entity, ranking, scope, country, partner, subtype]);
  useEffect(() => { load(); }, [load]);

  const isPlayers = entity === "players";
  const partnerMode = scope === "partner";
  const sportsbookMode = ranking === "sportsbook";
  const partnerSportsbookActivityOnly = false;  // Phase 4: sportsbook always rated
  const metricLabel = partnerMode ? (sportsbookMode ? "Settled" : "Rating") : (isPlayers ? METRIC_LABEL[ranking] : entity === "clubs" ? "Matches" : "Caps");
  // Normalise rows between the two APIs so the renderer stays simple.
  const normalized = partnerMode
    ? (data?.items || []).map((it) => ({
        position: it.partner_rank,
        change: null,
        rating: it.rating,
        matches_count: sportsbookMode ? (it.settled ?? it.activity?.settled ?? 0) : null,
        entity: {
          wsbf_player_id: it.player?.wsbf_player_id,
          display_name: it.player?.display_name,
          country: it.player?.country,
          is_simulated: it.player?.is_simulated,
          club: null,
        },
        world_rank: it.world_rank,
        maturity: it.maturity,
      }))
    : sportsbookMode
    ? (data?.items || []).map((it) => ({
        position: it.position,
        change: null,
        rating: it.rating,
        matches_count: it.settled ?? 0,
        entity: {
          wsbf_player_id: it.player?.wsbf_player_id,
          display_name: it.player?.display_name,
          country: it.player?.country,
          is_simulated: it.player?.is_simulated,
          club: null,
        },
        world_rank: null,
        maturity: it.maturity,
      }))
    : (data?.items || []);
  const total = data?.total ?? 0;
  const showPodium = isPlayers && !partnerMode && !sportsbookMode && skip === 0 && data && normalized.length >= 3;
  const tableItems = showPodium ? normalized.slice(3) : normalized;
  const sportsbookNote = false;

  return (
    <div className="animate-fade-up pb-16" data-testid="rankings-page">
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.18] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 140% at 50% 0%, rgba(212,175,55,0.14) 0%, transparent 55%)" }} />
        <div className={`${CONTAINER} relative py-12 sm:py-14`}>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-3">Global Rankings</div>
          <h1 className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">The World's Best</h1>
          <p className="text-slate-400 mt-3 text-sm sm:text-base max-w-2xl">
            Official WSTF competitive rankings — Players, Clubs and Nations. Partner scope shows the same official rankings filtered to a partner's linked population.
          </p>
        </div>
      </section>

      <div className={`${CONTAINER} pt-8`}>
        <div className="flex flex-col gap-5 rounded-2xl bg-[#0a0a0a] border border-[#2b2512] p-5" data-testid="ranking-filters">
          <div className="flex flex-wrap gap-6">
            {!partnerMode && (
              <FilterGroup label="Entity">
                <Seg options={ENTITIES} value={entity} onChange={setEntity} testidPrefix="entity" />
              </FilterGroup>
            )}
            <FilterGroup label="Scope">
              <Seg
                options={allowedScopes.map((s) => ({ key: s, label: s === "world" ? "Global" : s === "country" ? "Country" : "Partner" }))}
                value={scope} onChange={setScope} testidPrefix="scope" />
            </FilterGroup>
            <FilterGroup label="Ranking System">
              <Seg options={systems} value={ranking} onChange={setRanking} testidPrefix="ranking" />
            </FilterGroup>
            {scope === "country" && (
              <FilterGroup label="Country">
                <div className="relative">
                  <select data-testid="country-select" value={country} onChange={(e) => setCountry(e.target.value)}
                    className="appearance-none bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg pl-4 pr-10 py-2.5 text-sm text-slate-100 font-semibold focus:outline-none focus:border-[#E5B842]/70 min-w-[180px]">
                    {countries.length === 0 && <option value="">No countries</option>}
                    {countries.map((c) => <option key={c.iso3} value={c.iso3}>{c.name}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-[#E5B842] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </FilterGroup>
            )}
            {scope === "partner" && (
              <FilterGroup label="Partner">
                <div className="relative">
                  <select data-testid="partner-select" value={partner} onChange={(e) => setPartner(e.target.value)}
                    className="appearance-none bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg pl-4 pr-10 py-2.5 text-sm text-slate-100 font-semibold focus:outline-none focus:border-[#E5B842]/70 min-w-[200px]">
                    {partners.length === 0 && <option value="">No partners</option>}
                    {partners.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-[#E5B842] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </FilterGroup>
            )}
            {sportsbookMode && (
              <FilterGroup label="Sportsbook">
                <Seg options={SPORTSBOOK_SUBS} value={subtype} onChange={setSubtype} testidPrefix="subtype" />
              </FilterGroup>
            )}          </div>
          <div className="text-xs font-mono text-slate-500 flex items-center gap-2 flex-wrap" data-testid="rankings-descriptor">
            {partnerMode ? <Plug className="w-3.5 h-3.5 text-[#E5B842]" /> : <Globe className="w-3.5 h-3.5 text-[#E5B842]" />}
            {partnerMode
              ? (<span>Partner · <b className="text-slate-200">{partners.find((p) => p.slug === partner)?.name || "—"}</b> · {systems.find((s) => s.key === ranking)?.label}{sportsbookMode ? ` · ${subtype}` : ""} · <span className="text-slate-400">partner_rank preserves one official WSTF rating</span></span>)
              : (<span>{ENTITIES.find((e) => e.key === entity)?.label} · {systems.find((s) => s.key === ranking)?.label} · {scope === "world" ? "Global" : (countries.find((c) => c.iso3 === country)?.name || "Country")}</span>)}
          </div>
          {sportsbookNote && (
            <div className="text-[11px] font-mono text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="sportsbook-provisional-note">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Sportsbook activity view · WSTF Sportsbook rating formula is not yet defined. Values below are settled-event counts, not ratings.</span>
            </div>
          )}
        </div>
      </div>

      <div className={`${CONTAINER} pt-8`}>
        {error ? (
          <div data-testid="rankings-error" className="text-center py-20 border border-dashed border-rose-500/30 rounded-xl">
            <AlertTriangle className="w-7 h-7 text-rose-400 mx-auto mb-3" />
            <div className="text-slate-300 font-mono text-sm uppercase tracking-widest mb-4">Could not load rankings</div>
            <button onClick={load} data-testid="rankings-retry" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[rgba(212,175,55,0.3)] text-slate-200 hover:border-[#E5B842]/60 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
              <RotateCw className="w-4 h-4" /> Retry
            </button>
          </div>
        ) : !data ? (
          <div data-testid="rankings-skeleton" className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-14 rounded-lg bg-[#0f0f0f] border border-[#2b2512] animate-pulse" />)}
          </div>
        ) : normalized.length === 0 ? (
          <div data-testid="rankings-empty" className="text-center py-20 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.25)] rounded-xl">
            {partnerMode ? "No linked players ranked for this view" : `No ranked ${entity} for the selected combination`}
          </div>
        ) : (
          <>
            <div className="text-[11px] font-mono text-slate-500 mb-3" data-testid="rankings-count">
              {total} ranked · showing {skip + 1}–{Math.min(skip + PAGE, total)}
            </div>

            {showPodium && <Podium items={normalized.slice(0, 3)} metricLabel={metricLabel} />}

            <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] overflow-hidden">
              <table className="w-full text-sm" data-testid="rankings-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-[#070707]">
                    <th className="text-left px-5 py-3.5 w-20">Pos</th>
                    {!partnerMode && <th className="text-left px-2 py-3.5 w-16">Move</th>}
                    <th className="text-left px-2 py-3.5">{entity === "clubs" ? "Club" : entity === "nations" ? "Nation" : "Player"}</th>
                    <th className="text-left px-2 py-3.5 hidden sm:table-cell">{entity === "clubs" ? "Country" : entity === "nations" ? "ID" : (partnerMode ? "Country" : "Club")}</th>
                    {partnerMode && <th className="text-right px-2 py-3.5 hidden sm:table-cell">World</th>}
                    <th className="text-right px-2 py-3.5">{sportsbookMode ? "Settled" : "Rating"}</th>
                    {!partnerMode && <th className="text-right px-5 py-3.5 hidden md:table-cell">{metricLabel}</th>}
                  </tr>
                </thead>
                <tbody>
                  {(tableItems || []).map((it) => (
                    <RankRow key={`${it.position}-${it.entity?.wsbf_player_id || it.entity?.club_id || it.entity?.country_iso3 || Math.random()}`} it={it} entity={entity} partnerMode={partnerMode} sportsbookMode={sportsbookMode} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4">
              <button data-testid="rankings-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} className="px-4 py-2 rounded-lg text-sm font-mono bg-[#0f0f0f] border border-[#2b2512] text-slate-300 disabled:opacity-40 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>← Prev</button>
              <span className="text-xs font-mono text-slate-500">Page {Math.floor(skip / PAGE) + 1} / {Math.max(1, Math.ceil(total / PAGE))}</span>
              <button data-testid="rankings-next" disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)} className="px-4 py-2 rounded-lg text-sm font-mono bg-[#0f0f0f] border border-[#2b2512] text-slate-300 disabled:opacity-40 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>Next →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Podium({ items, metricLabel }) {
  const order = [items[1], items[0], items[2]].filter(Boolean);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 items-end" data-testid="rankings-podium">
      {order.map((it) => {
        const e = it.entity || {};
        const big = it.position === 1;
        return (
          <Link key={it.position} to={`/player/${e.wsbf_player_id}`} data-testid={`podium-${it.position}`}
            className={`group relative block overflow-hidden rounded-2xl foil-border ${big ? "sm:-mt-6" : ""}`}
            style={{ aspectRatio: big ? "5 / 6" : "5 / 6.4" }}>
            <img src={POS_ART[it.position - 1]} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105" style={{ transition: "transform 0.5s ease" }} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[rgba(0,0,0,0.5)] to-transparent" />
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <Trophy className={`w-5 h-5 ${it.position === 1 ? "text-[#FFD700]" : it.position === 2 ? "text-slate-300" : "text-[#CB8E4E]"}`} />
              <span className={`font-heading font-black leading-none ${big ? "text-5xl" : "text-4xl"}`} style={{ color: it.position === 1 ? "#FFD700" : it.position === 2 ? "#D7DBE1" : "#CB8E4E" }}>#{it.position}</span>
            </div>
            <div className="absolute inset-x-0 bottom-0 p-5">
              <div className="flex items-center gap-2">
                <span className="font-heading text-2xl font-black uppercase tracking-tight text-slate-50 truncate wsbf-slant-block"><Flag country={e.country} width={20} className="mr-1.5 align-middle" />{e.display_name}</span>
                {e.is_simulated && <SimBadge />}
              </div>
              <div className="text-xs text-slate-400 mt-1 font-mono">
                {e.club ? (e.club.short_name || e.club.name) : "Free agent"} · {e.country?.name}
              </div>
              <div className="flex items-center gap-4 mt-3">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">Rating</div>
                  <RatingValue value={it.rating} className="text-3xl" />
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">{metricLabel}</div>
                  <div className="font-heading text-xl font-bold text-slate-200 tabular-nums">{it.matches_count}</div>
                </div>
                <div className="ml-auto"><RankChange change={it.change} testid={`podium-change-${it.position}`} /></div>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function RankRow({ it, entity, partnerMode, sportsbookMode }) {
  const e = it.entity || {};
  const top = it.position <= 3;
  let nameCell;
  if (entity === "nations") {
    nameCell = (
      <Link to={`/nations/${e.country_iso3}`} className="flex items-center gap-2 text-slate-100 hover:text-[#E5B842] font-semibold" style={{ transition: "color 0.15s ease" }}>
        <Flag country={e} width={18} className="mr-0.5" /> {e.name}
      </Link>
    );
  } else if (entity === "clubs") {
    nameCell = (
      <Link to={`/club/${e.club_id}`} className="flex items-center gap-2.5 text-slate-100 hover:text-[#E5B842] font-semibold" style={{ transition: "color 0.15s ease" }}>
        <span className="w-7 h-7 rounded-md grid place-items-center text-[10px] font-heading font-black text-[#000000] shrink-0" style={{ background: e.color_primary || "#E5B842" }}>{(e.short_name || e.name || "?").slice(0, 3).toUpperCase()}</span>
        <span className="truncate inline-flex items-center gap-1.5"><Flag country={e.country} width={16} />{e.name}</span>
        {e.is_simulated && <SimBadge />}
      </Link>
    );
  } else {
    nameCell = (
      <Link to={`/player/${e.wsbf_player_id}`} className="flex items-center gap-2.5 text-slate-100 hover:text-[#E5B842] font-semibold" style={{ transition: "color 0.15s ease" }}>
        <CinematicPortrait player={e} variant="thumb" className="w-7 h-9 shrink-0" testid={`ranking-photo-${e.wsbf_player_id}`} />
        <span className="inline-flex items-center gap-1.5"><Flag country={e.country} width={16} />{e.display_name}
        {e.is_simulated && <SimBadge />}</span>
      </Link>
    );
  }
  return (
    <tr data-testid={`ranking-row-${it.position}`} className={`border-t border-[#2b2512]/60 hover:bg-[#161616]/60 ${top ? "bg-[#1a1407]/30" : ""}`} style={{ transition: "background-color 0.15s ease" }}>
      <td className="px-5 py-3.5">
        <span className={`font-heading font-black text-lg ${top ? "text-[#E5B842]" : "text-slate-200"}`}>#{it.position}</span>
      </td>
      {!partnerMode && <td className="px-2 py-3.5"><RankChange change={it.change} testid={`change-${it.position}`} /></td>}
      <td className="px-2 py-3.5">{nameCell}</td>
      <td className="px-2 py-3.5 text-slate-400 hidden sm:table-cell">
        {entity === "nations" ? e.wsbf_nt_id : entity === "clubs" ? e.country?.name : (partnerMode ? (e.country?.name || "—") : (e.club ? (e.club.short_name || e.club.name) : <span className="text-slate-600 italic">Free agent</span>))}
      </td>
      {partnerMode && <td className="px-2 py-3.5 text-right hidden sm:table-cell"><span className="font-mono text-slate-400 tabular-nums" data-testid={`world-rank-${it.position}`}>{it.world_rank ? `#${it.world_rank}` : "—"}</span></td>}
      <td className="px-2 py-3.5 text-right">
        {sportsbookMode
          ? <span className="font-heading font-black text-base text-[#E5B842] tabular-nums" data-testid={`activity-${it.position}`}>{it.matches_count ?? 0}</span>
          : <RatingValue value={it.rating} className="text-base" />}
      </td>
      {!partnerMode && <td className="px-5 py-3.5 text-right font-mono text-slate-400 hidden md:table-cell">{it.matches_count}</td>}
    </tr>
  );
}
